// Calcoli del business plan di stagione (02/11/2026). Tutto qui, puro: dati dal server + ipotesi → risultato.
// Regola del titolare: Q1 + Q2 devono pagare tutto l'anno; l'estivo è un di più.
// Gli annuali pagano una volta (in Q1): al rinnovo Q2 non portano soldi nuovi ma costano ancora (campi, allenatori).

export const MESI_NOMI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const GIORNO = 86400000;

export const stagioneCorrente = () => { const d = new Date(); return d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1; };
export const mesiStagione = (st) => Array.from({ length: 12 }, (_, i) => { const m = (8 + i) % 12; const y = m >= 8 ? st : st + 1; return `${y}-${String(m + 1).padStart(2, '0')}`; });
export const nomeMese = (k) => { const [y, m] = k.split('-'); return `${MESI_NOMI[+m - 1]} ${y.slice(2)}`; };
const iso = (d) => (d ? String(d).slice(0, 10) : null);
const settimaneTra = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / GIORNO / 7));

// Ipotesi di partenza: ciò che non si legge dal gestionale lo decide il titolare, qui i valori prudenti di base.
// Nome sede → chiave dei contratti (come il server: senza accenti, maiuscolo, spazi singoli)
export const chiaveSede = (n) => String(n || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();
export const contrattoDi = (srv, n) => (srv.contratti || {})[chiaveSede(n)] || null;
// Costo campi dei corsi preso dai campi dedicati di Contratti strutture
export const sedeDalContratto = (srv, n) => {
  const c = contrattoDi(srv, n);
  return {
    manuale: false,
    modalita: c?.corsi_modalita === 'FORFAIT' ? 'CONTRATTO' : 'ORARIO',
    costo_ora: c?.corsi_costo_ora ?? '',
    contratto_annuo: c?.corsi_forfait ?? '',
  };
};

export const ipotesiIniziali = (srv) => {
  const st = srv.stagione;
  const q1 = srv.periodi.find(p => p.periodo === 'Q1');
  const q2 = srv.periodi.find(p => p.periodo === 'Q2');
  const es = srv.periodi.find(p => p.periodo === 'ESTIVO');
  const quadr = q1 ? q1.sedi.reduce((a, s) => a + s.quadrimestrali, 0) : 0;
  const dovQ = q1 ? q1.sedi.reduce((a, s) => a + s.dovuto_quadrimestrali, 0) : 0;
  const q1i = iso(q1?.data_inizio) || `${st}-09-28`, q1f = iso(q1?.data_fine) || `${st + 1}-01-31`;
  const q2i = iso(q2?.data_inizio) || `${st + 1}-02-01`, q2f = iso(q2?.data_fine) || `${st + 1}-05-31`;
  const esi = iso(es?.data_inizio) || `${st + 1}-06-01`, esf = iso(es?.data_fine) || `${st + 1}-07-31`;
  const sedi = {};
  const nomiSedi = new Set(srv.periodi.flatMap(p => p.sedi.map(s => s.nome)));
  nomiSedi.forEach(n => { sedi[n] = sedeDalContratto(srv, n); });
  return {
    periodi: {
      Q1: { inizio: q1i, fine: q1f, settimane: Math.max(1, settimaneTra(q1i, q1f) - 2) },  // -2 = vacanze di Natale
      Q2: { inizio: q2i, fine: q2f, settimane: Math.max(1, settimaneTra(q2i, q2f) - 1) },  // -1 = Pasqua
      ESTIVO: { inizio: esi, fine: esf, settimane: settimaneTra(esi, esf) },
    },
    durata_ore: 1.5,
    rinnovo_pct: 75,
    nuovi_q2: 0,
    prezzo_q2: quadr ? Math.round(dovQ / quadr) : 0,
    attivita_q2_pct: 100,
    incasso_q2_primo_mese_pct: 70,
    estivo: { attivo: false, iscritti: '', prezzo: '', attivita_pct: 30 },
    sedi,
    fissi_mesi: 12,
    spese: [],
    future: [],
    entrate_extra: [],
    tornei: [],   // [{ id, sede, numero, campi, ore, incasso, spese }] — i tornei sono "in più", come l'estivo
    riserva_mesi: 2,
    cassa_iniziale: '',
  };
};

// Unisce ipotesi salvate e nuove sedi comparse nel planning
export const completaIpotesi = (salvate, srv) => {
  const base = ipotesiIniziali(srv);
  if (!salvate) return base;
  const out = { ...base, ...salvate, periodi: { ...base.periodi, ...(salvate.periodi || {}) }, estivo: { ...base.estivo, ...(salvate.estivo || {}) }, sedi: { ...base.sedi, ...(salvate.sedi || {}) } };
  ['spese', 'future', 'entrate_extra', 'tornei'].forEach(k => { if (!Array.isArray(out[k])) out[k] = []; });
  // Le sedi non cambiate a mano seguono sempre il contratto (se il contratto cambia, il piano si aggiorna)
  Object.keys(out.sedi).forEach(n => { if (!out.sedi[n] || !out.sedi[n].manuale) out.sedi[n] = sedeDalContratto(srv, n); });
  return out;
};

// Numeri scritti all'italiana: "1.500" = millecinquecento, "1,5" e "1.5" = uno e mezzo
const num = (v) => {
  if (v === '' || v == null) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  let t = String(v).trim().replace(/[€\s]/g, '');
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : 0;
};
export { num };

// Distribuisce un importo sui mesi della stagione in proporzione ai giorni del periodo [a, b]
const spalma = (importo, a, b, mesi) => {
  const out = {};
  const da = new Date(a), al = new Date(b);
  const tot = (al - da) / GIORNO + 1;
  if (!(tot > 0) || !importo) return out;
  mesi.forEach(k => {
    const [y, m] = k.split('-').map(Number);
    const ini = new Date(Date.UTC(y, m - 1, 1)), fin = new Date(Date.UTC(y, m, 0));
    const s = Math.max(ini, da), e = Math.min(fin, al);
    const g = (e - s) / GIORNO + 1;
    if (g > 0) out[k] = importo * g / tot;
  });
  return out;
};
const somma = (acc, parti) => { Object.entries(parti).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; }); };

export const calcola = (srv, ip) => {
  const mesi = mesiStagione(srv.stagione);
  const per = (p) => srv.periodi.find(x => x.periodo === p);
  const q1 = per('Q1'), q2 = per('Q2'), es = per('ESTIVO');
  const sedi1 = q1?.sedi || [];
  const D = num(ip.durata_ore) || 1.5;
  const w1 = num(ip.periodi.Q1.settimane), w2 = num(ip.periodi.Q2.settimane), we = num(ip.periodi.ESTIVO.settimane);
  const fQ2 = num(ip.attivita_q2_pct) / 100, fEs = num(ip.estivo.attivita_pct) / 100;
  const avvisi = [];

  // --- Ricavi ---
  const annuali = sedi1.reduce((a, s) => a + s.dovuto_annuali, 0);
  const quadrQ1 = sedi1.reduce((a, s) => a + s.dovuto_quadrimestrali, 0);
  const nQuadr = sedi1.reduce((a, s) => a + s.quadrimestrali, 0);
  const quoteQ1 = q1?.quote_tesseramento || 0;
  const incassoQ1 = annuali + quadrQ1 + quoteQ1;
  const pagatoQ1 = sedi1.reduce((a, s) => a + s.pagato, 0);
  const q2Reale = q2 && q2.sedi.some(s => s.avviato);
  const rinnovi = Math.round(nQuadr * num(ip.rinnovo_pct) / 100) + Math.round(num(ip.nuovi_q2));
  const prezzoQ2 = num(ip.prezzo_q2);
  const incassoQ2 = q2Reale
    ? q2.sedi.reduce((a, s) => a + s.dovuto_quadrimestrali + s.dovuto_annuali, 0) + (q2.quote_tesseramento || 0)
    : rinnovi * prezzoQ2;
  const esReale = es && es.sedi.some(s => s.avviato);
  const incassoEs = !ip.estivo.attivo ? 0 : esReale
    ? es.sedi.reduce((a, s) => a + s.dovuto_quadrimestrali + s.dovuto_annuali, 0) + (es.quote_tesseramento || 0)
    : num(ip.estivo.iscritti) * num(ip.estivo.prezzo);
  const extra = ip.entrate_extra.reduce((a, e) => a + num(e.importo), 0);

  // --- Costi diretti dei corsi, per sede (campi + allenatori) ---
  const perSede = sedi1.map(s => {
    const cfg = ip.sedi[s.nome] || { modalita: 'ORARIO', costo_ora: '' };
    const oreSett = s.gruppi * D;
    const campoOra = cfg.modalita === 'ORARIO' ? num(cfg.costo_ora) : 0;
    // 03/11: il costo del contratto vale per SLOT (una lezione da 1,5 h su un campo), non per ora
    const campiQ1 = cfg.modalita === 'ORARIO' ? s.gruppi * w1 * campoOra : 0;
    const campiQ2 = cfg.modalita === 'ORARIO' ? s.gruppi * w2 * fQ2 * campoOra : 0;
    const contratto = cfg.modalita === 'CONTRATTO' ? num(cfg.contratto_annuo) : 0;
    // 03/11 cifra fissa (Robbiate): copre i campi tutto l'anno. Ai corsi va solo ciò che usano (slot × valore di uno slot);
    // il resto sono campi già pagati e liberi (Natale, estate, slot vuoti) da sfruttare: restano nel piano generale.
    const valSlot = cfg.modalita === 'CONTRATTO' ? num(cfg.costo_ora) : 0;
    const usoQ1 = !contratto ? 0 : valSlot ? Math.min(contratto, s.gruppi * w1 * valSlot) : contratto / 2;
    const usoQ2 = !contratto ? 0 : valSlot ? Math.min(contratto - usoQ1, s.gruppi * w2 * fQ2 * valSlot) : contratto / 2;
    const contrattoLibero = contratto - usoQ1 - usoQ2;
    const slotAnno = valSlot ? contratto / valSlot : 0, slotUsati = s.gruppi * (w1 + w2 * fQ2);
    const allQ1 = s.tariffe_ora_settimana * D * w1;
    const allQ2 = s.tariffe_ora_settimana * D * w2 * fQ2;
    if (cfg.modalita === 'ORARIO' && !campoOra) avvisi.push({ tipo: 'attenzione', testo: `${s.nome}: manca il costo del campo a slot. Finché è vuoto il piano conta i campi gratis.` });
    if (cfg.modalita === 'CONTRATTO' && !contratto) avvisi.push({ tipo: 'attenzione', testo: `${s.nome}: contratto annuo a 0 €. Scrivi quanto costa la struttura per la stagione.` });
    if (s.allenatori_non_trovati.length) avvisi.push({ tipo: 'info', testo: `${s.nome}: ${s.allenatori_non_trovati.join(', ')} non ha una scheda collaboratore: uso la tariffa media.` });
    if (s.gruppi_allenatore_fisso) avvisi.push({ tipo: 'info', testo: `${s.nome}: ${s.gruppi_allenatore_fisso} gruppi con un allenatore a compenso fisso: nel margine della sede (scheda SEDI) pesa la quota del suo fisso; nel totale resta tra i collaboratori fissi.` });
    const ricaviCompetenza = s.dovuto_quadrimestrali + s.dovuto_annuali / 2;
    // allenatori a compenso fisso che allenano qui: quota del loro fisso dei mesi del Q1, divisa per i gruppi che allenano
    const fissiQ1 = (srv.collaboratori_fissi || []).reduce((a, c) => {
      const tot = Object.values(c.gruppi_per_sede || {}).reduce((x, y) => x + y, 0);
      const qui = (c.gruppi_per_sede || {})[s.nome] || 0;
      return tot ? a + num(c.mensile) * (w1 + 2) / 4.33 * qui / tot : a;
    }, 0);
    const costiQ1 = campiQ1 + allQ1 + fissiQ1 + usoQ1;
    // Q2 della sede (03/11, rapporto "solo sedi"): metà annuali + quadrimestrali del Q2
    // (veri dal planning se il Q2 è avviato, altrimenti rinnovi della sede × prezzo medio Q2, nuovi divisi in proporzione)
    const s2 = q2Reale ? q2.sedi.find(x => x.nome === s.nome) : null;
    const rinnoviSede = nQuadr ? (s.quadrimestrali * num(ip.rinnovo_pct) / 100 + num(ip.nuovi_q2) * s.quadrimestrali / nQuadr) : 0;
    const quadrQ2 = q2Reale ? (s2 ? s2.dovuto_quadrimestrali + s2.dovuto_annuali : 0) : rinnoviSede * prezzoQ2;
    const ricaviQ2 = s.dovuto_annuali / 2 + quadrQ2;
    const fissiQ2 = (srv.collaboratori_fissi || []).reduce((a, c) => {
      const tot = Object.values(c.gruppi_per_sede || {}).reduce((x, y) => x + y, 0);
      const qui = (c.gruppi_per_sede || {})[s.nome] || 0;
      return tot ? a + num(c.mensile) * w2 / 4.33 * qui / tot : a;
    }, 0);
    const costiQ2 = campiQ2 + allQ2 + fissiQ2 + usoQ2;
    return {
      nome: s.nome, gruppi: s.gruppi, atleti: s.annuali + s.quadrimestrali, oreSett,
      annuali: s.annuali, quadrimestrali: s.quadrimestrali, rinnoviQ2: q2Reale ? null : rinnoviSede,
      campiQ1, campiQ2, contratto, usoQ1, usoQ2, contrattoLibero, valSlot, slotAnno, slotUsati, allQ1, allQ2, fissiQ1, fissiQ2,
      ricaviQ1: ricaviCompetenza, costiQ1, margineQ1: ricaviCompetenza - costiQ1,
      ricaviQ2, costiQ2, margineQ2: ricaviQ2 - costiQ2,
    };
  });
  const campiQ1 = perSede.reduce((a, s) => a + s.campiQ1, 0), campiQ2 = perSede.reduce((a, s) => a + s.campiQ2, 0);
  const contratti = perSede.reduce((a, s) => a + s.contratto, 0);
  const allQ1 = perSede.reduce((a, s) => a + s.allQ1, 0), allQ2 = perSede.reduce((a, s) => a + s.allQ2, 0);
  const settEs = sedi1.reduce((a, s) => a + s.tariffe_ora_settimana * D, 0) + perSede.reduce((a, s) => a + s.gruppi * (num((ip.sedi[s.nome] || {}).costo_ora) * ((ip.sedi[s.nome] || {}).modalita === 'ORARIO' ? 1 : 0)), 0);
  const costiEs = ip.estivo.attivo ? settEs * we * fEs : 0;

  // --- Tornei (in più, come l'estivo): incasso − spese − campi dal contratto della sede ---
  const tornei = (ip.tornei || []).map(t => {
    const c = contrattoDi(srv, t.sede);
    const n = num(t.numero);
    const giornata = c?.tornei_modalita === 'GIORNATA';
    const campiTorneo = !c ? 0 : giornata ? num(c.tornei_costo) : num(t.campi) * num(t.ore) * num(c.tornei_costo);
    if (n > 0 && !c) avvisi.push({ tipo: 'attenzione', testo: `Tornei a ${t.sede || '(sede non scelta)'}: non c'è in Contratti strutture, i campi contano gratis.` });
    else if (n > 0 && c.tornei_costo == null) avvisi.push({ tipo: 'attenzione', testo: `Tornei a ${t.sede}: manca il costo dei campi per i tornei in Contratti strutture, contano gratis.` });
    const ricavi = n * num(t.incasso), costi = n * (num(t.spese) + campiTorneo);
    return { id: t.id, sede: t.sede, numero: n, giornata, campiTorneo, ricavi, costi, margine: ricavi - costi };
  });
  const torneiRicavi = tornei.reduce((a, t) => a + t.ricavi, 0), torneiCosti = tornei.reduce((a, t) => a + t.costi, 0);

  // --- Costi di struttura ---
  const fissiMese = srv.collaboratori_fissi.reduce((a, c) => a + num(c.mensile), 0);
  // La stagione vera parte col Q1 (fine settembre/ottobre): i compensi fissi si pagano da quel mese.
  // Se il Q1 inizia dopo il 15, il primo mese pieno è il successivo.
  const q1Ini = String(ip.periodi.Q1.inizio || '');
  let idxAvvio = Math.max(0, mesi.indexOf(q1Ini.slice(0, 7)));
  if (+q1Ini.slice(8, 10) > 15) idxAvvio = Math.min(11, idxAvvio + 1);
  const fissiMesi = Math.min(12 - idxAvvio, Math.max(0, num(ip.fissi_mesi)));
  const meseAvvioFissi = mesi[idxAvvio];
  const fissi = fissiMese * fissiMesi;
  const ricorrenti = ip.spese.reduce((a, s) => a + num(s.importo) * (s.frequenza === 'MENSILE' ? 12 : 1), 0);
  const future = ip.future.reduce((a, s) => a + num(s.importo), 0);
  if (!ip.spese.length) avvisi.push({ tipo: 'attenzione', testo: 'Non hai ancora inserito spese ricorrenti (commercialista, tessere, affiliazioni, servizi…): il piano è troppo ottimista.' });

  const costiCorsi = campiQ1 + campiQ2 + contratti + allQ1 + allQ2;
  const costiAnno = costiCorsi + fissi + ricorrenti + future; // senza estivo: lo devono pagare Q1+Q2
  const riserva = num(ip.riserva_mesi) * costiAnno / 12;
  const obiettivo = costiAnno + riserva;
  const dopoQ1 = obiettivo - incassoQ1;                       // quanto deve portare Q2 (+ entrate extra)
  const servonoQ2 = Math.max(0, dopoQ1 - extra);
  const rinnoviNecessari = prezzoQ2 > 0 ? Math.ceil(servonoQ2 / prezzoQ2) : null;
  const pctNecessaria = nQuadr > 0 && rinnoviNecessari != null ? Math.round(rinnoviNecessari / nQuadr * 100) : null;
  const ricaviQ1Q2 = incassoQ1 + incassoQ2 + extra;
  const risultato = ricaviQ1Q2 - costiAnno;                   // prima della riserva
  const libero = risultato - riserva + (incassoEs - costiEs) + (torneiRicavi - torneiCosti);

  // --- Competenza per periodo (gli annuali valgono metà Q1, metà Q2) ---
  const periodi = [
    { nome: 'Q1', ricavi: quadrQ1 + annuali / 2 + quoteQ1, costi: campiQ1 + allQ1 + contratti / 2 },
    { nome: 'Q2', ricavi: (q2Reale ? incassoQ2 : incassoQ2) + annuali / 2, costi: campiQ2 + allQ2 + contratti / 2 },
    { nome: 'Estivo', ricavi: incassoEs, costi: costiEs, extra: true },
    ...(tornei.length ? [{ nome: 'Tornei', ricavi: torneiRicavi, costi: torneiCosti, extra: true }] : []),
  ];

  // --- Quadrimestre per quadrimestre (03/11/2026, titolare: "devo capire nei vari quadrimestri, rispetto alle spese
  // del periodo, come sono messo"). Mesi: Q1 = da settembre al mese di fine Q1, Q2 = fino al mese di fine Q2, estate = il resto.
  // Entrate di competenza (annuali metà e metà), spese del periodo: corsi + compensi fissi + spese ricorrenti/future nei loro mesi.
  const fineQ1 = String(ip.periodi.Q1.fine || '').slice(0, 7), fineQ2 = String(ip.periodi.Q2.fine || '').slice(0, 7);
  const periodoMese = (k) => (fineQ1 && k <= fineQ1 ? 0 : fineQ2 && k <= fineQ2 ? 1 : 2);
  const fissiP = [0, 0, 0], speseP = [0, 0, 0], extraP = [0, 0, 0];
  mesi.slice(idxAvvio, idxAvvio + fissiMesi).forEach(k => { fissiP[periodoMese(k)] += fissiMese; });
  ip.spese.forEach(x => {
    if (x.frequenza === 'MENSILE') mesi.forEach(k => { speseP[periodoMese(k)] += num(x.importo); });
    else { const k = mesi.find(m => +m.slice(5) === num(x.mese)) || mesi[0]; speseP[periodoMese(k)] += num(x.importo); }
  });
  ip.future.forEach(x => { const k = mesi.includes(x.mese) ? x.mese : mesi[0]; speseP[periodoMese(k)] += num(x.importo); });
  ip.entrate_extra.forEach(x => { const k = mesi.includes(x.mese) ? x.mese : mesi[0]; extraP[periodoMese(k)] += num(x.importo); });
  const nomiMesi = (i) => { const m = mesi.filter(k => periodoMese(k) === i); return m.length ? `${nomeMese(m[0])} – ${nomeMese(m[m.length - 1])}` : ''; };
  let progressivo = 0;
  const quadrimestri = [
    // entrate = annuali metà e metà (quanto rende il periodo); incasso = quando arrivano i soldi (annuali tutti nel Q1)
    { nome: 'Q1', entrate: quadrQ1 + annuali / 2 + quoteQ1, incasso: incassoQ1, campi: campiQ1 + contratti / 2, allenatori: allQ1 },
    { nome: 'Q2', entrate: incassoQ2 + annuali / 2, incasso: incassoQ2, campi: campiQ2 + contratti / 2, allenatori: allQ2, stima: !q2Reale },
    { nome: 'Estate', entrate: incassoEs, incasso: incassoEs, campi: costiEs, allenatori: 0, stima: ip.estivo.attivo && !esReale },
  ].map((q, i) => {
    const corsi = q.campi + q.allenatori;
    const entrate = q.entrate + extraP[i], incasso = q.incasso + extraP[i], spese = corsi + fissiP[i] + speseP[i];
    progressivo += entrate - spese;
    return { ...q, corsi, mesi: nomiMesi(i), entrate, incasso, altre_entrate: extraP[i], fissi: fissiP[i], ricorrenti: speseP[i], spese, risultato: entrate - spese, risultato_cassa: incasso - spese, progressivo };
  });
  let progCassa = 0; quadrimestri.forEach(q => { progCassa += q.risultato_cassa; q.progressivo_cassa = progCassa; });

  // --- Cassa mese per mese ---
  const ent = {}, usc = {};
  const P = ip.periodi;
  // Q1: pagamenti già registrati nel loro mese, il resto ancora da incassare nel mese prossimo
  const dp = new Date(); dp.setDate(1); dp.setMonth(dp.getMonth() + 1);
  const meseProssimo = `${dp.getFullYear()}-${String(dp.getMonth() + 1).padStart(2, '0')}`;
  Object.entries(srv.pagamenti_mese).forEach(([k, v]) => { if (mesi.includes(k)) ent[k] = (ent[k] || 0) + v; else if (k < mesi[0]) ent[mesi[0]] = (ent[mesi[0]] || 0) + v; });
  // il resto ancora da incassare: metà questo mese e metà il prossimo (se siamo fuori stagione, nel primo mese del Q1)
  const residuoQ1 = Math.max(0, incassoQ1 - pagatoQ1);
  const dq = new Date(); const meseOra = `${dq.getFullYear()}-${String(dq.getMonth() + 1).padStart(2, '0')}`;
  if (mesi.includes(meseOra)) {
    ent[meseOra] = (ent[meseOra] || 0) + residuoQ1 / 2;
    const k2 = mesi.includes(meseProssimo) ? meseProssimo : meseOra;
    ent[k2] = (ent[k2] || 0) + residuoQ1 / 2;
  } else {
    const k = mesi.find(x => x >= String(P.Q1.inizio).slice(0, 7)) || mesi[1];
    ent[k] = (ent[k] || 0) + residuoQ1;
  }
  if (incassoQ2) {
    const m1 = String(P.Q2.inizio).slice(0, 7); const i1 = mesi.indexOf(m1);
    const pr = num(ip.incasso_q2_primo_mese_pct) / 100;
    if (i1 >= 0) { ent[m1] = (ent[m1] || 0) + incassoQ2 * pr; const m2 = mesi[Math.min(11, i1 + 1)]; ent[m2] = (ent[m2] || 0) + incassoQ2 * (1 - pr); }
  }
  if (incassoEs) { const m = String(P.ESTIVO.inizio).slice(0, 7); if (mesi.includes(m)) ent[m] = (ent[m] || 0) + incassoEs; }
  ip.entrate_extra.forEach(e => { if (mesi.includes(e.mese)) ent[e.mese] = (ent[e.mese] || 0) + num(e.importo); });

  somma(usc, spalma(campiQ1 + allQ1, P.Q1.inizio, P.Q1.fine, mesi));
  somma(usc, spalma(campiQ2 + allQ2, P.Q2.inizio, P.Q2.fine, mesi));
  somma(usc, spalma(contratti, P.Q1.inizio, P.Q2.fine, mesi));
  if (costiEs) somma(usc, spalma(costiEs, P.ESTIVO.inizio, P.ESTIVO.fine, mesi));
  // tornei: entrate e uscite spalmate su tutta la stagione
  const st0 = `${srv.stagione}-09-01`, st1 = `${srv.stagione + 1}-08-31`;
  if (torneiRicavi) somma(ent, spalma(torneiRicavi, st0, st1, mesi));
  if (torneiCosti) somma(usc, spalma(torneiCosti, st0, st1, mesi));
  mesi.slice(idxAvvio, idxAvvio + fissiMesi).forEach(k => { usc[k] = (usc[k] || 0) + fissiMese; });
  ip.spese.forEach(s => {
    if (s.frequenza === 'MENSILE') mesi.forEach(k => { usc[k] = (usc[k] || 0) + num(s.importo); });
    else { const k = mesi.find(x => +x.slice(5) === num(s.mese)) || mesi[0]; usc[k] = (usc[k] || 0) + num(s.importo); }
  });
  ip.future.forEach(s => { const k = mesi.includes(s.mese) ? s.mese : mesi[0]; usc[k] = (usc[k] || 0) + num(s.importo); });

  // Debiti/crediti della stagione scorsa pagati quest'anno (scheda STAGIONE SCORSA): pesano sulla cassa nel loro mese,
  // ma non sono costi di questa stagione (niente obiettivo, niente "puoi ancora spendere")
  const preg = srv.pregressi || [];
  preg.forEach(t => {
    const k = String(t.data).slice(0, 7); const m = mesi.includes(k) ? k : mesi[0];
    if (t.importo < 0) usc[m] = (usc[m] || 0) - t.importo; else ent[m] = (ent[m] || 0) + t.importo;
  });
  const pregUscite = -preg.filter(t => t.importo < 0).reduce((a, t) => a + t.importo, 0);
  const pregEntrate = preg.filter(t => t.importo > 0).reduce((a, t) => a + t.importo, 0);

  const cassaIniziale = ip.cassa_iniziale !== '' && ip.cassa_iniziale != null ? num(ip.cassa_iniziale) : (srv.cassa.inizio_stagione ?? 0);
  const cassaLibera = cassaIniziale - pregUscite + pregEntrate;
  if (preg.length && pregEntrate > pregUscite) avvisi.push({ tipo: 'info', testo: `Quest'anno sono arrivati ${Math.round(pregEntrate - pregUscite).toLocaleString('it-IT')} € della stagione scorsa (scheda STAGIONE SCORSA): sono in più, non contano come incassi di questa stagione.` });
  else if (preg.length) avvisi.push({ tipo: 'info', testo: `Dei ${Math.round(cassaIniziale).toLocaleString('it-IT')} € in cassa al 1° settembre, ${Math.round(pregUscite - pregEntrate).toLocaleString('it-IT')} € erano della stagione scorsa (scheda STAGIONE SCORSA): i soldi davvero liberi a inizio stagione erano ${Math.round(cassaLibera).toLocaleString('it-IT')} €.` });
  let saldo = cassaIniziale;
  const cassa = mesi.map(k => { saldo += (ent[k] || 0) - (usc[k] || 0); return { mese: k, entrate: ent[k] || 0, uscite: usc[k] || 0, saldo, sottoRiserva: saldo < riserva }; });
  const minimo = cassa.reduce((m, c) => (c.saldo < m.saldo ? c : m), cassa[0]);
  if (minimo && minimo.saldo < 0) avvisi.unshift({ tipo: 'attenzione', testo: `A ${nomeMese(minimo.mese)} la cassa andrebbe sotto zero (${Math.round(minimo.saldo).toLocaleString('it-IT')} €): servono incassi prima o spese spostate.` });

  return {
    mesi, avvisi,
    ricavi: { annuali, quadrQ1, nQuadr, quoteQ1, incassoQ1, pagatoQ1, incassoQ2, q2Reale, rinnovi, incassoEs, extra, ricaviQ1Q2 },
    costi: { campiQ1, campiQ2, contratti, allQ1, allQ2, costiEs, fissi, fissiMese, fissiMesi, meseAvvioFissi, ricorrenti, future, costiCorsi, costiAnno },
    riserva, obiettivo, servonoQ2, rinnoviNecessari, pctNecessaria, risultato, libero,
    periodi, quadrimestri, perSede, cassa, cassaIniziale, cassaLibera, minimo,
    pregressi: { n: preg.length, uscite: pregUscite, entrate: pregEntrate },
    tornei: { righe: tornei, ricavi: torneiRicavi, costi: torneiCosti, margine: torneiRicavi - torneiCosti },
  };
};

// Risultato con un'altra percentuale di rinnovo (scenari)
export const scenario = (srv, ip, pct) => calcola(srv, { ...ip, rinnovo_pct: pct });
